import { Box, makeStyles } from '@material-ui/core';
import React, { useEffect } from 'react';
import { FunctionField, List, Record, ReferenceField, ReduxState, useLoading, useTranslate } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import { types } from '../../pages/paths';
import {
    GROUP_METADATA_AVATAR_TYPES,
    MEDIA_TAG_INSTALLING_GUIDE,
    MEDIA_TAG_REFLEX_EMPTYING,
    MEDIA_TAG_REFLEX_MAINTENANCE,
    MEDIA_TAG_USER_BOOKLET,
    MEDIA_TAG_USER_GUIDE,
    METADATA_AVATAR_TYPE,
    METADATA_AVATAR_TYPES,
} from '../../providers/resources';
import { addToMetadataAvatarTypeData, selectType } from '../../redux/actions/types.actions';
import { CustomReducerState } from '../../redux/reducers';
import { tableStyles } from '../../theme/customTheme';
import { getNameWithoutPrefix } from '../../utils/resources/MetadataAvatarType';
import { MetadataAvatarType } from '../../utils/types';
import DataGridCustom from '../UI/DataGrid';
import MediaCell from '../UI/MediaCell/MediaCell';
import ListActions from './actions/managementTypes';
import EnableApprovalPropertyField from './fields/EnableApprovalPropertyField';
import FiltersList from './filters/FiltersList';
import '../../styles/list.css';
import { PAGE_MAX_WIDTH } from '../../theme/crmColors';

const styles = () => ({
    ...tableStyles(),
    pageContainer: {
        display: 'grid',
        gap: 16,
        padding: 24,
        width: '100%',
        maxWidth: PAGE_MAX_WIDTH,
        margin: '0 auto',
        boxSizing: 'border-box',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gridTemplateRows: 'auto auto 1fr',
        height: '100%',
        minHeight: '100%',
        '@media (max-width:600px)': {
            gap: 8,
            padding: 8,
        },
    },
});
//@ts-ignore
const useStyles = makeStyles(styles);
const TypesList = () => {
    const t = useTranslate();
    const classes = useStyles();
    const dispatch = useDispatch();
    const loading = useLoading();
    const selectedMAT: MetadataAvatarType | Record | null = useSelector(
        (state: ReduxState & CustomReducerState) => {
            const selectedMetadataAvatarTypeUri = (
                state.rikutec.types.selected as MetadataAvatarType
            )['@id'];
            return selectedMetadataAvatarTypeUri &&
                selectedMetadataAvatarTypeUri in state.admin.resources[METADATA_AVATAR_TYPES].data
                ? state.admin.resources[METADATA_AVATAR_TYPES].data[selectedMetadataAvatarTypeUri]
                : null;
        },
    );

    const listProps = {
        resource: METADATA_AVATAR_TYPES,
        hasCreate: true,
        hasEdit: true,
        hasList: true,
        hasShow: false,
        basePath: types,
        sort: { field: 'createdAt', order: 'ASC' },
        perPage: 10,
        filter: {
            serialization_groups: ['metadata_avatar_type_files'],
        },
    };
    // noinspection JSUnusedGlobalSymbols
    const dataGridProps = {
        rowClick: (id: string, basePath: string, record: Record) => {
            dispatch(selectType(record));
            return null;
        },
        toFindSelected: {
            resource: 'types',
            key: 'selected',
        },
        hasBulkActions: null,
        classes: {
            checkbox: classes.checkbox,
            row: classes.list,
        },
    };
    useEffect(() => {
        if (
            selectedMAT &&
            selectedMAT.hasOwnProperty('@id') &&
            !loading &&
            //eslint-disable-next-line
            selectedMAT.files !== selectedMAT.files
        ) {
            dispatch(selectType(selectedMAT['@id']));
        }
    }, [loading, dispatch, selectedMAT]);
    return (
        <Box className={classes.pageContainer}>
            <Box className='list'>
                <List
                    component='div'
                    {...listProps}
                    filters={<FiltersList />}
                    actions={<ListActions />}
                    bulkActionButtons={false}
                >
                    <DataGridCustom {...dataGridProps}>
                            <FunctionField
                                label={t('app.types.productName')}
                                source='name'
                                sortable={true}
                                // @ts-ignore
                                render={(record: MetadataAvatarType) => `${getNameWithoutPrefix(record.name)}`}
                            />

                            <FunctionField
                                sortable={true}
                                label={t('app.types.approvalEnabled')}
                                // @ts-ignore
                                render={(record: MetadataAvatarType | undefined) =>
                                    //@ts-ignore
                                    record ? <EnableApprovalPropertyField record={record} /> : <span>{'...'}</span>
                                }
                            />

                            <ReferenceField
                                sortable={true}
                                label={t('app.types.productType')}
                                reference={GROUP_METADATA_AVATAR_TYPES}
                                source='groupMetadataAvatarType'
                                sortBy='groupMetadataAvatarType.name'
                                link={false}
                            >
                                <FunctionField
                                    sortable={true}
                                    // @ts-ignore
                                    render={(record: MetadataAvatarType) => `${getNameWithoutPrefix(record.name)}`}
                                />
                            </ReferenceField>
                            <FunctionField
                                label={t('app.types.installing_guide')}
                                render={(record: Record | undefined) => (
                                    <MediaCell
                                        record={record!}
                                        mediaTag={MEDIA_TAG_INSTALLING_GUIDE}
                                        objectType={METADATA_AVATAR_TYPE}
                                        reduxUpdater={addToMetadataAvatarTypeData}
                                        mediasObjectContainer='files'
                                        storeContainer='types'
                                    />
                                )}
                            />
                            <FunctionField
                                label={t('app.types.user_guide')}
                                render={(record: Record | undefined) => (
                                    <MediaCell
                                        record={record!}
                                        mediaTag={MEDIA_TAG_USER_GUIDE}
                                        objectType={METADATA_AVATAR_TYPE}
                                        reduxUpdater={addToMetadataAvatarTypeData}
                                        mediasObjectContainer='files'
                                        storeContainer='types'
                                    />
                                )}
                            />
                            <FunctionField
                                label={t('app.types.user_booklet')}
                                render={(record: Record | undefined) => (
                                    <MediaCell
                                        record={record!}
                                        mediaTag={MEDIA_TAG_USER_BOOKLET}
                                        objectType={METADATA_AVATAR_TYPE}
                                        reduxUpdater={addToMetadataAvatarTypeData}
                                        mediasObjectContainer='files'
                                        storeContainer='types'
                                    />
                                )}
                            />
                            {/*<FunctionField*/}
                            {/*    label={t('app.types.technical_sheet')}*/}
                            {/*    render={(record: Record | undefined) => (*/}
                            {/*        <TypeMedias record={record} mediaTag='technical_sheet' />*/}
                            {/*    )}*/}
                            {/*/>*/}
                            {/*<FunctionField*/}
                            {/*    label={t('app.types.routine_maintenance')}*/}
                            {/*    render={(record: Record | undefined) => (*/}
                            {/*        <TypeMedias record={record} mediaTag='routine_maintenance' />*/}
                            {/*    )}*/}
                            {/*/>*/}
                            <FunctionField
                                label={t('app.types.reflex_emtying')}
                                render={(record: Record | undefined) => (
                                    <MediaCell
                                        record={record!}
                                        mediaTag={MEDIA_TAG_REFLEX_EMPTYING}
                                        objectType={METADATA_AVATAR_TYPE}
                                        reduxUpdater={addToMetadataAvatarTypeData}
                                        mediasObjectContainer='files'
                                        storeContainer='types'
                                    />
                                )}
                            />
                            <FunctionField
                                label={t('app.types.reflex_maintenance')}
                                render={(record: Record | undefined) => (
                                    <MediaCell
                                        record={record!}
                                        mediaTag={MEDIA_TAG_REFLEX_MAINTENANCE}
                                        objectType={METADATA_AVATAR_TYPE}
                                        reduxUpdater={addToMetadataAvatarTypeData}
                                        mediasObjectContainer='files'
                                        storeContainer='types'
                                    />
                                )}
                            />
                    </DataGridCustom>
                </List>
            </Box>
        </Box>
    );
};

export default TypesList;
